/**
 * Calendar dates (birthdays, document issue dates) have no time component.
 * Store them as UTC midnight so serialization and PDF formatting stay stable across timezones.
 * Mirrors packages/website/src/components/DateInput.vue.
 */

const MS_PER_DAY = 86_400_000;

/** Normalize an API/stored value to UTC midnight for the intended calendar day. */
export function fromCalendarDate(value: Date | string): Date {
  const date = typeof value === 'string' ? new Date(value) : value;
  const ms = date.getTime();
  if (Number.isNaN(ms)) return date;
  // Round to nearest UTC midnight. Correct for new UTC-midnight values and legacy
  // local-midnight values that shifted when serialized (e.g. 22:00Z for DE midnight).
  return new Date(Math.round(ms / MS_PER_DAY) * MS_PER_DAY);
}

/** Convert a datepicker selection (local calendar day) to UTC midnight for API storage. */
export function toCalendarDate(date: Date): Date {
  const result = new Date();
  result.setUTCHours(0, 0, 0, 0);
  result.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
  return result;
}

/** Convert a stored calendar date to local midnight for datepicker display. */
export function toLocalDateForPicker(value: Date | string): Date {
  const date = fromCalendarDate(value);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** Format a calendar date for display using UTC (matches website DateInput). */
export function formatCalendarDate(value: Date | string, format: string): string {
  const date = fromCalendarDate(value);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  if (format === 'dd.MM.yyyy') {
    return `${day}.${month}.${year}`;
  }
  if (format === 'MM/dd/yyyy') {
    return `${month}/${day}/${year}`;
  }

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
    calendar: 'gregory',
    numberingSystem: 'latn',
  }).format(date);
}
